import { addressParser } from 'postal-mime';

import {
  type Communication,
  type CommunicationAttachment,
  CommunicationAttachmentSchema,
  type CommunicationPagination,
  type CommunicationParticipant,
  type EmailCommunication,
  EmailCommunicationSchema,
  type MeetingCommunication,
  MeetingCommunicationSchema,
  type MeetingMinutesCommunication,
  MeetingMinutesCommunicationSchema,
  SiteSchema,
} from '../entities/communication';

export async function communicationAttachmentMapper(
  data: any,
): Promise<CommunicationAttachment> {
  return CommunicationAttachmentSchema.parseAsync({
    id: data.file_id,
    name: data.file_name?.split('/').slice(-1).join('/'),
    contentType: data.content_type,
  });
}

export async function emailCommunicationMapper(
  data: any,
): Promise<EmailCommunication> {
  const attachments = await attachmentsFromAttachments(data.attachments);

  const linkedSites = linkedSitesFromSites(data.sites);

  return EmailCommunicationSchema.parseAsync({
    id: data.uuid,
    date: new Date(data.date),
    type: 'email',
    subject: data.subject ?? '',
    body: data.body ?? '',
    sender: parseParticipants(data.sender)[0],
    recipients: parseParticipants(data.recipients),
    cc: parseParticipants(data.cc),
    attachments,
    linkedSites,
  });
}

export async function meetingCommunicationMapper(
  data: any,
): Promise<MeetingCommunication> {
  const attachments = await attachmentsFromAttachments(data.attachments);
  const meetingMinutes =
    data.associated_meeting_minutes?.map((item: any) => ({
      id: item.uuid,
      title: item.subject ?? undefined,
      content: item.content,
    })) ?? [];
  const linkedSites = linkedSitesFromSites(data.sites);

  return MeetingCommunicationSchema.parseAsync({
    id: data.uuid,
    date: new Date(data.meeting_metadata?.start_time ?? data.date),
    type: 'meeting',
    title: data.meeting_metadata?.title ?? '',
    start: data.meeting_metadata
      ? new Date(data.meeting_metadata.start_time)
      : undefined,
    end: data.meeting_metadata
      ? new Date(data.meeting_metadata.end_time)
      : undefined,
    location: data.meeting_metadata?.location ?? undefined,
    attendees: parseParticipants(data.meeting_metadata?.participants ?? ''),
    summary: data.body ?? '',
    attachments,
    meetingMinutes,
    linkedSites,
  });
}

export async function meetingMinutesCommunication(
  data: any,
): Promise<MeetingMinutesCommunication> {
  return MeetingMinutesCommunicationSchema.parseAsync({
    id: data.uuid,
    date: new Date(data.date),
    type: 'meeting-minutes',
    title: data.subject ?? undefined,
    content: data.content ?? '',
    meeting: data.email_uuid
      ? {
          id: data.email_uuid,
          title: data.email_subject,
        }
      : undefined,
  });
}

export async function communicationMapper(
  data: any,
): Promise<Communication | undefined> {
  if (data.type) {
    if (data.type === 'regular') {
      return emailCommunicationMapper(data);
    }
    if (data.type === 'meeting') {
      return meetingCommunicationMapper(data);
    }
  }

  return undefined;
}

function nextCursorFromNext(next: string | null): number | undefined {
  if (!next) {
    return undefined;
  }

  const url = new URL(next);
  const offset = Number(url.searchParams.get('offset'));

  if (Number.isNaN(offset)) {
    return undefined;
  }

  return offset;
}

export async function communicationPaginationMapper(
  data: any,
): Promise<CommunicationPagination> {
  const settled = await Promise.allSettled(
    data.results?.map((data: any) => communicationMapper(data)) ?? [],
  );

  const communications = settled.flatMap((result) => {
    if (result.status !== 'fulfilled') {
      return [];
    }
    return [result.value];
  });

  const filteredCommunications = communications.filter(filterCommunication);

  return {
    totalCount: data.count,
    nextCursor: nextCursorFromNext(data.next),
    communications: filteredCommunications,
  };
}

function filterCommunication(
  communication: Communication | undefined,
): communication is Communication {
  return Boolean(communication);
}

function parseParticipants(
  participants: string = '',
): CommunicationParticipant[] {
  return addressParser(participants).map((result) => ({
    name: result.name || undefined,
    address: result.address || undefined,
  }));
}

function linkedSitesFromSites(sites: unknown) {
  if (!Array.isArray(sites)) {
    return [];
  }

  return sites.flatMap((site) => {
    const record = site as Record<string, unknown>;

    const id =
      typeof record.accure_id === 'string'
        ? record.accure_id
        : typeof record.id === 'string'
          ? record.id
          : undefined;

    const name = typeof record.name === 'string' ? record.name : undefined;

    const parsed = SiteSchema.safeParse({ id, name });

    if (!parsed.success) {
      return [];
    }

    return [parsed.data];
  });
}

async function attachmentsFromAttachments(
  attachments: unknown,
): Promise<CommunicationAttachment[]> {
  if (!Array.isArray(attachments)) {
    return [];
  }

  const settled = await Promise.allSettled(
    attachments.map((data) => communicationAttachmentMapper(data)),
  );

  return settled.flatMap((result) => {
    if (result.status !== 'fulfilled') {
      return [];
    }
    return [result.value];
  });
}
